import { z } from "zod";
import {
  HeroFormatSchema,
  LintReportSchema,
  SeasonContentSchema,
} from "@scrile/domain/season";
import { HeroPricingSchema, HeroThemeSchema } from "./hero.js";

/**
 * Контракт студии креатора и панели платформы: `/api/v1/studio/*` и `/api/v1/admin/*`.
 * Тот же вход и та же сессия, что у подписчика; доступ решает роль
 * (`creators` для студии, `users.role = admin` для панели). Студия не привязана к
 * хосту героя — живёт по адресу STUDIO_URL (`https://studio.<BASE_DOMAIN>` или под
 * путём: `https://ai.scrile.com/studio`), героя выбирают в адресе маршрута по `slug`.
 * Пути ниже — внутри студии, от её базы.
 */

export const HeroStatusSchema = z.enum(["draft", "review", "live", "paused"]);
export type HeroStatus = z.infer<typeof HeroStatusSchema>;

export const CreatorTierSchema = z.enum(["standard", "studio"]);

/**
 * Служебные поддомены платформы: героя с таким слагом завести нельзя (slug = поддомен).
 * `new` — адрес мастера создания героя в студии (/heroes/new).
 */
export const RESERVED_SLUGS = [
  "www",
  "api",
  "app",
  "studio",
  "admin",
  "media",
  "static",
  "cdn",
  "assets",
  "mail",
  "help",
  "support",
  "status",
  "blog",
  "docs",
  "new",
] as const;

/**
 * GET /api/v1/studio/me. Вошедший без записи в creators получает 403 — и сотрудник
 * платформы тоже: роль для панели фронт узнаёт из GET /api/v1/me (MeSchema.role).
 */
export const CreatorMeSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  tier: CreatorTierSchema,
  /** admin — креатор, который ещё и сотрудник платформы: студии показать панель. */
  role: z.enum(["creator", "admin"]),
  /** Прошёл ли проверку Stripe Connect; без неё выплаты копятся. */
  payoutsReady: z.boolean(),
  /** Своя ссылка креатора: подписчик по ней закрепляется за ним. */
  referralCode: z.string(),
  heroes: z.array(
    z.object({
      slug: z.string(),
      name: z.string(),
      format: HeroFormatSchema,
      status: HeroStatusSchema,
      subscribers: z.number().int().nonnegative(),
      url: z.string(),
      /** Портрет из темы героя, для списка героев. */
      portraitUrl: z.string().nullable(),
    }),
  ),
});
export type CreatorMe = z.infer<typeof CreatorMeSchema>;

/** Персона героя: то, из чего собирается образ в переписке и голос в аудио. */
export const HeroPersonaSchema = z.object({
  /** Кто он: возраст, занятие, характер — 2–6 предложений. */
  bio: z.string().max(2000),
  /** Как говорит: длина реплик, тон, любимые обороты. */
  voiceStyle: z.string().max(1000),
  /** Чего герой не делает никогда — поверх правил платформы. */
  boundaries: z.string().max(1000),
  /** Только chat: как возвращать к сюжету, если человек ушёл в сторону. */
  steering: z.string().max(1000).optional(),
});
export type HeroPersona = z.infer<typeof HeroPersonaSchema>;

/** Цвет темы — только #RRGGBB: из него шаблон считает вторую схему и контраст. */
export const HexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Use a color like #E4572E");

/** Тема в черновике героя — строже публичной: цвета проверяются на входе студии. */
export const HeroThemeDraftSchema = HeroThemeSchema.extend({
  accent: HexColorSchema,
  background: HexColorSchema,
  surface: HexColorSchema,
  text: HexColorSchema,
});

/**
 * Цены в черновике: не меньше 50 центов (минимум Stripe для USD), пробный период —
 * 0–90 дней (дольше Stripe не даёт у подписки).
 */
export const MIN_PRICE_CENTS = 50;
export const MAX_TRIAL_DAYS = 90;
export const HeroPricingDraftSchema = HeroPricingSchema.extend({
  monthlyCents: z.number().int().min(MIN_PRICE_CENTS),
  yearlyCents: z.number().int().min(MIN_PRICE_CENTS),
  allAtOnceCents: z.number().int().min(MIN_PRICE_CENTS).nullable(),
  trialDays: z.number().int().min(0).max(MAX_TRIAL_DAYS),
});

export const HeroDraftSchema = z.object({
  /**
   * Поддомен героя. Служебные (RESERVED_SLUGS) сервер отвергает с 400 — в схеме их
   * проверки нет намеренно: форма студии показывает для них свой текст, а не общий
   * «неверный адрес».
   */
  slug: z
    .string()
    .min(3)
    .max(32)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
  name: z.string().min(1).max(60),
  format: HeroFormatSchema,
  tagline: z.string().max(140),
  description: z.string().max(4000),
  persona: HeroPersonaSchema,
  theme: HeroThemeDraftSchema,
  pricing: HeroPricingDraftSchema,
  /** Идентификатор голоса у поставщика TTS; null — не выбран (публиковать нельзя). */
  voiceId: z.string().nullable(),
  language: z.string().min(2).max(8),
  repliesEnabled: z.boolean(),
  trailerAssetId: z.string().nullable(),
});
export type HeroDraft = z.infer<typeof HeroDraftSchema>;

/** GET /api/v1/studio/heroes/:slug */
export const StudioHeroSchema = HeroDraftSchema.extend({
  id: z.string(),
  status: HeroStatusSchema,
  url: z.string(),
  freeEpisodes: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StudioHero = z.infer<typeof StudioHeroSchema>;

/** GET /api/v1/studio/heroes — герои креатора (POST на тот же путь — создать, ответ 201 StudioHero). */
export const StudioHeroesListSchema = z.object({ heroes: z.array(StudioHeroSchema) });

/**
 * PATCH /api/v1/studio/heroes/:slug — любое подмножество черновика, кроме slug и format.
 * Ответ — StudioHeroSchema.
 */
export const HeroPatchSchema = HeroDraftSchema.omit({ slug: true, format: true }).partial();

/** POST /api/v1/studio/heroes/:slug/status. Ответ — StudioHeroSchema после перехода. */
export const HeroStatusActionSchema = z.object({
  action: z.enum(["submit", "pause", "resume"]),
});
/** Ответ POST .../status — герой после перехода. */
export const HeroStatusResponseSchema = StudioHeroSchema;

/**
 * GET /api/v1/studio/voices — голоса для героя: из ElevenLabs (/v1/voices) при ключе,
 * из мока без ключа; сервер кеширует список на час.
 */
export const VoiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Короткий образец голоса у поставщика; null — образца нет. */
  previewUrl: z.string().nullable(),
  /** Язык образца (en, es…) — подсказка, а не ограничение: голоса многоязычные. */
  language: z.string(),
});
export type Voice = z.infer<typeof VoiceSchema>;
export const VoicesResponseSchema = z.object({ voices: z.array(VoiceSchema) });

/**
 * Переписка героя (волна 3, dialogue): когда отвечать голосом — never; to_voice — голосом
 * на голосовое подписчика; always — голосом всегда (в пределах дневного потолка и лимита
 * расхода, иначе текстом).
 */
export const DIALOGUE_VOICE_REPLIES = ["never", "to_voice", "always"] as const;
export const DialogueVoiceRepliesSchema = z.enum(DIALOGUE_VOICE_REPLIES);

/**
 * Настройки живого ответа героя. PUT /api/v1/studio/heroes/:slug/dialogue — любое
 * подмножество; ответ — HeroDialogueSettingsResponseSchema.
 */
export const HeroDialogueSettingsSchema = z.object({
  /** Модель OpenRouter из списка models; null — умолчание платформы. */
  model: z.string().min(1).max(120).nullable(),
  voiceReplies: DialogueVoiceRepliesSchema,
  /** Голосовых ответов подписчику за сутки; null — умолчание платформы. */
  voiceDailyLimit: z.number().int().min(0).max(100).nullable(),
});
export type HeroDialogueSettings = z.infer<typeof HeroDialogueSettingsSchema>;
export const HeroDialogueSettingsPatchSchema = HeroDialogueSettingsSchema.partial();

/** GET и ответ PUT: настройки и то, из чего выбирать. */
export const HeroDialogueSettingsResponseSchema = HeroDialogueSettingsSchema.extend({
  /** Модели, которые можно выбрать (первая — умолчание платформы). */
  models: z.array(z.string()),
  defaultModel: z.string(),
  defaultVoiceDailyLimit: z.number().int().nonnegative(),
});
export type HeroDialogueSettingsResponse = z.infer<typeof HeroDialogueSettingsResponseSchema>;

/** Медиатека героя: файлы для сцен, фоны, обложки. */
export const AssetKindSchema = z.enum(["image", "video", "sound", "background", "cover", "trailer"]);
export const AssetSchema = z.object({
  id: z.string(),
  /** Имя, на которое ссылается файл сезона; уникально у героя, в NFC. */
  name: z.string(),
  kind: AssetKindSchema,
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  durationSec: z.number().nonnegative().nullable(),
  origin: z.enum(["creator_upload", "tts", "mix", "ai_generated"]),
  url: z.string(),
  createdAt: z.string(),
});
export type Asset = z.infer<typeof AssetSchema>;

/** GET /api/v1/studio/heroes/:slug/assets — голый массив. DELETE .../assets/:id — 204. */
export const AssetsListResponseSchema = z.array(AssetSchema);

/** POST /api/v1/studio/heroes/:slug/assets — multipart: поле `file`, поля `name`, `kind`. Ответ 201 Asset. */
export const AssetUploadFieldsSchema = z.object({
  name: z.string().min(1).max(120),
  kind: AssetKindSchema,
});

export const RevisionStatusSchema = z.enum(["draft", "review", "published", "archived"]);

/**
 * Последнее решение модерации по ревизии: pending — ждёт проверки, rejected — с
 * комментарием креатору. null — ревизию на проверку не отправляли.
 */
export const RevisionModerationSchema = z.object({
  decision: z.enum(["pending", "approved", "rejected"]),
  comment: z.string().nullable(),
  decidedAt: z.string().nullable(),
});

export const RevisionSummarySchema = z.object({
  id: z.string(),
  season: z.number().int().positive(),
  revision: z.number().int().positive(),
  status: RevisionStatusSchema,
  title: z.string(),
  episodes: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
  warnings: z.number().int().nonnegative(),
  /** Озвучено ли всё, что нужно для публикации (audio). */
  rendered: z.boolean(),
  createdAt: z.string(),
  publishedAt: z.string().nullable(),
  moderation: RevisionModerationSchema.nullable(),
});

/** GET /api/v1/studio/heroes/:slug/seasons */
export const SeasonsListSchema = z.object({
  seasons: z.array(
    z.object({
      season: z.number().int().positive(),
      status: RevisionStatusSchema,
      revisions: z.array(RevisionSummarySchema),
    }),
  ),
});

/**
 * POST /api/v1/studio/heroes/:slug/seasons/import — тело: файл сезона как текст.
 * Ответ 201 (ревизия записана) или 200 (dryRun, ошибки разбора). Если ревизия этого
 * сезона сейчас на проверке — 409 conflict: сначала решение модератора или снятие
 * ревизии с проверки (archive), иначе модератор смотрел бы не то, что опубликуется.
 */
export const SeasonImportRequestSchema = z.object({
  yaml: z.string().min(1).max(2_000_000),
  /** Только показать, что изменится, ничего не сохранять. */
  dryRun: z.boolean().default(false),
});

export const SeasonDiffSchema = z.object({
  /** Ключи реплик: сколько новых, сколько изменённых, сколько убранных против прошлой ревизии. */
  linesAdded: z.number().int().nonnegative(),
  linesChanged: z.number().int().nonnegative(),
  linesRemoved: z.number().int().nonnegative(),
  episodesBefore: z.number().int().nonnegative(),
  episodesAfter: z.number().int().nonnegative(),
  /** Сколько знаков придётся озвучить заново (audio). */
  voiceCharsToRender: z.number().int().nonnegative(),
});

export const SeasonImportResponseSchema = z.object({
  /** null при dryRun или при ошибках разбора. */
  revision: RevisionSummarySchema.nullable(),
  lint: LintReportSchema,
  diff: SeasonDiffSchema.nullable(),
});
export type SeasonImportResponse = z.infer<typeof SeasonImportResponseSchema>;

/** GET /api/v1/studio/heroes/:slug/seasons/:season/revisions/:revision */
export const RevisionDetailSchema = z.object({
  summary: RevisionSummarySchema,
  content: SeasonContentSchema,
  source: z.string().nullable(),
  lint: LintReportSchema,
  /** Озвучка по сериям: готово / в очереди / ошибка. */
  render: z.array(
    z.object({
      kind: z.enum(["episode", "rubric", "voice_note"]),
      ref: z.string(),
      /**
       * not_requested — озвучку ещё не просили (ни кнопки, ни публикации); pending — в
       * очереди; rendering — собирается; ready — общая дорожка готова; failed — ошибка.
       */
      state: z.enum(["not_requested", "pending", "rendering", "ready", "failed"]),
      durationSec: z.number().nonnegative().nullable(),
      /** Подписанный адрес общей дорожки для прослушивания в студии. */
      previewUrl: z.string().nullable(),
      error: z.string().nullable(),
    }),
  ),
});
export type RevisionDetail = z.infer<typeof RevisionDetailSchema>;

/** POST .../revisions/:revision/render — поставить озвучку в очередь (audio). Ответ 202. */
/** POST .../revisions/:revision/submit — отправить на проверку. Ответ 204. */
/** POST .../revisions/:revision/archive — снять черновик. Ответ 204. */

/** GET /api/v1/studio/heroes/:slug/stats?days=30 */
export const HeroStatsSchema = z.object({
  days: z.number().int().positive(),
  subscribers: z.object({
    trialing: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    pastDue: z.number().int().nonnegative(),
    canceled: z.number().int().nonnegative(),
  }),
  bySource: z.object({
    creator: z.number().int().nonnegative(),
    partner: z.number().int().nonnegative(),
    platform: z.number().int().nonnegative(),
  }),
  funnel: z.object({
    pageViews: z.number().int().nonnegative(),
    pilotStarted: z.number().int().nonnegative(),
    pilotFinished: z.number().int().nonnegative(),
    trials: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
  }),
  /** Удержание по сериям: сколько начали и сколько дослушали. */
  episodes: z.array(
    z.object({
      ordinal: z.number().int().positive(),
      started: z.number().int().nonnegative(),
      finished: z.number().int().nonnegative(),
    }),
  ),
  revenue: z.object({
    grossCents: z.number().int().nonnegative(),
    creatorCents: z.number().int(),
  }),
});
export type HeroStats = z.infer<typeof HeroStatsSchema>;

/** Промокоды креатора: продлённый пробный период или скидка. */
export const PromoCodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  heroSlug: z.string().nullable(),
  /** Бонус к пробному периоду героя, дни: не больше 30 — промокод не бесплатный доступ. */
  trialBonusDays: z.number().int().nonnegative().max(30),
  discountPercent: z.number().int().min(0).max(50),
  maxUses: z.number().int().positive().nullable(),
  uses: z.number().int().nonnegative(),
  expiresAt: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
});
/** POST /api/v1/studio/codes — ответ 201 PromoCode. */
export const PromoCodeDraftSchema = PromoCodeSchema.omit({ id: true, uses: true, createdAt: true }).extend({
  code: z
    .string()
    .min(4)
    .max(24)
    .regex(/^[A-Z0-9-]+$/),
});
/** GET /api/v1/studio/codes — голый массив кодов креатора (без его своей ссылки). */
export const PromoCodesResponseSchema = z.array(PromoCodeSchema);
/** PATCH /api/v1/studio/codes/:id — любое подмножество черновика; ответ — PromoCode. */
export const PromoCodePatchSchema = PromoCodeDraftSchema.partial();

/** GET /api/v1/studio/payouts */
export const PayoutsSchema = z.object({
  /** Начислено и ещё не выплачено, центы. */
  pendingCents: z.number().int(),
  /** Порог выплаты. */
  minimumCents: z.number().int().nonnegative(),
  periods: z.array(
    z.object({
      period: z.string(),
      baseCents: z.number().int(),
      acquisitionCents: z.number().int(),
      tipsCents: z.number().int(),
      clawbackCents: z.number().int(),
      totalCents: z.number().int(),
      status: z.enum(["accruing", "pending", "paid", "held"]),
      paidAt: z.string().nullable(),
    }),
  ),
});

/** POST /api/v1/studio/connect — ссылка на онбординг Stripe Connect. */
export const ConnectResponseSchema = z.object({ url: z.string() });

/**
 * Куда Stripe возвращает креатора в студию (STUDIO_URL + путь): после онбординга
 * Connect и при истёкшей ссылке онбординга (тогда студия запрашивает новую).
 */
export const STUDIO_RETURN_PATHS = {
  connectReturn: "/payouts?connect=done",
  connectRefresh: "/payouts?connect=refresh",
} as const;

/** Панель платформы. */
export const ModerationItemSchema = z.object({
  id: z.string(),
  heroSlug: z.string(),
  heroName: z.string(),
  creatorName: z.string(),
  season: z.number().int().positive(),
  revision: z.number().int().positive(),
  submittedAt: z.string(),
  warnings: z.number().int().nonnegative(),
});

/** GET /api/v1/admin/moderation */
export const ModerationQueueSchema = z.object({ items: z.array(ModerationItemSchema) });

/**
 * GET /api/v1/admin/moderation/:id — пункт очереди с героем и ревизией целиком: админ
 * читает чужую ревизию только так (маршруты студии отдают лишь своих героев).
 */
export const ModerationItemDetailSchema = z.object({
  item: ModerationItemSchema,
  hero: StudioHeroSchema,
  revision: RevisionDetailSchema,
});

/** POST /api/v1/admin/moderation/:id */
export const ModerationDecisionSchema = z.object({
  decision: z.enum(["approve", "reject"]),
  /** Комментарий креатору; при отказе обязателен. */
  comment: z.string().max(2000).optional(),
});

/** POST /api/v1/admin/creators/invite */
export const CreatorInviteSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(60),
  tier: CreatorTierSchema.default("standard"),
});

/**
 * Ответ 201 на приглашение. Пароля нет ни в ответе, ни в базе: креатор задаёт его сам
 * по одноразовой ссылке (7 дней). Ссылку шлёт письмом notifications (событие
 * creator.invited), а до писем — передаёт администратор. Повторное приглашение того же
 * креатора, пока он не принял прежнее, выдаёт новую ссылку и гасит старую.
 */
export const CreatorInviteResponseSchema = z.object({
  id: z.string(),
  slug: z.string(),
  email: z.string(),
  name: z.string(),
  tier: CreatorTierSchema,
  status: z.literal("invited"),
  /** Почта уже была зарегистрирована: пароль, заданный по ссылке, станет паролем аккаунта. */
  existingAccount: z.boolean(),
  /** STUDIO_URL + /invite?token=… — одноразовая, 7 дней. */
  inviteUrl: z.string(),
});

/** Страница студии, на которую ведёт ссылка приглашения (токен — в ?token=); от базы студии. */
export const STUDIO_INVITE_PATH = "/invite";

/**
 * POST /api/v1/studio/invite/accept — принять приглашение по токену из ссылки: пароль
 * аккаунта, согласие с текущими условиями, креатор становится active, ставится сессия.
 * Ответ 200 — CreatorMeSchema. Неверная, использованная или истёкшая ссылка — 400.
 */
export const InviteAcceptRequestSchema = z.object({
  token: z.string().min(16).max(200),
  password: z.string().min(8).max(200),
});

export const STUDIO_ROUTES = {
  me: "/api/v1/studio/me",
  /** GET — герои креатора (StudioHeroesListSchema), POST — новый герой (HeroDraftSchema). */
  heroes: "/api/v1/studio/heroes",
  hero: (slug: string) => `/api/v1/studio/heroes/${slug}`,
  heroStatus: (slug: string) => `/api/v1/studio/heroes/${slug}/status`,
  /** GET — список (AssetsListResponseSchema), POST — загрузка (multipart). */
  assets: (slug: string) => `/api/v1/studio/heroes/${slug}/assets`,
  /** DELETE — убрать файл из медиатеки (204; 409, если он нужен ревизии или теме). */
  asset: (slug: string, id: string) => `/api/v1/studio/heroes/${slug}/assets/${id}`,
  seasons: (slug: string) => `/api/v1/studio/heroes/${slug}/seasons`,
  seasonImport: (slug: string) => `/api/v1/studio/heroes/${slug}/seasons/import`,
  revision: (slug: string, season: number, revision: number) =>
    `/api/v1/studio/heroes/${slug}/seasons/${season}/revisions/${revision}`,
  revisionRender: (slug: string, season: number, revision: number) =>
    `/api/v1/studio/heroes/${slug}/seasons/${season}/revisions/${revision}/render`,
  revisionSubmit: (slug: string, season: number, revision: number) =>
    `/api/v1/studio/heroes/${slug}/seasons/${season}/revisions/${revision}/submit`,
  revisionArchive: (slug: string, season: number, revision: number) =>
    `/api/v1/studio/heroes/${slug}/seasons/${season}/revisions/${revision}/archive`,
  stats: (slug: string) => `/api/v1/studio/heroes/${slug}/stats`,
  /** GET — голоса поставщика озвучки (VoicesResponseSchema). */
  voices: "/api/v1/studio/voices",
  /** GET — настройки переписки героя, PUT — правка (HeroDialogueSettingsPatchSchema). */
  heroDialogue: (slug: string) => `/api/v1/studio/heroes/${slug}/dialogue`,
  /** GET — список (PromoCodesResponseSchema), POST — новый код (PromoCodeDraftSchema). */
  codes: "/api/v1/studio/codes",
  /** PATCH — правка кода (PromoCodePatchSchema) → PromoCode. */
  code: (id: string) => `/api/v1/studio/codes/${id}`,
  payouts: "/api/v1/studio/payouts",
  connect: "/api/v1/studio/connect",
  adminModeration: "/api/v1/admin/moderation",
  /** GET — просмотр пункта (ModerationItemDetailSchema), POST — решение (ModerationDecisionSchema, 204). */
  adminModerationItem: (id: string) => `/api/v1/admin/moderation/${id}`,
  adminCreatorsInvite: "/api/v1/admin/creators/invite",
  /** POST — принять приглашение по токену (InviteAcceptRequestSchema) → CreatorMe, 200. */
  inviteAccept: "/api/v1/studio/invite/accept",
} as const;
